import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, ViewEncapsulation, WritableSignal, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { UserInfo } from '@app-types/user-info';
import { ColleagueDisplayComponent } from '@components/colleague-display/colleague-display.component';
import { UserPendingLabelComponent } from '@components/user-pending-label/user-pending-label.component';
import { TranslocoModule } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';
import { SpacerComponent } from 'src/app/common/components/spacer/spacer.component';
import { ApiCallInjectDirective } from 'src/app/common/directives/api-call-inject.directive';
import { ApiCallModule } from 'src/app/common/directives/api-call.module';
import iconLibrary from 'src/app/common/iconLibrary';
import { WritableApiSignal, refetchApiSignal, toWritableApiSignal } from 'src/app/common/util/api-signal';
import { DialogService } from 'src/app/common/util/dialog-service';
import { Events } from 'src/app/util/events';
import { userInfoInputFields } from 'src/app/util/user-info-input-fields';
import { UserInfoInputConfig } from 'src/app/util/user-info-input-groupt';
import { ShadowStylesDirective } from 'src/boilerplate/shadow-styles.directive';
import { ColleagueInfo, ColleaguesService, UserService } from 'src/openapi-client';
import { EditInfoDialogComponent } from './edit-info-dialog/edit-info-dialog.component';
import { ProfileSectionComponent } from './profile-section/profile-section.component';

@Component({
  selector: 'wct-profile-account',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    ProfileSectionComponent,
    ColleagueDisplayComponent,
    UserPendingLabelComponent,
    SpacerComponent,
    ApiCallModule,
    ApiCallInjectDirective,
  ],
  templateUrl: './profile-account.component.html',
  styleUrls: ['./profile-account.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  hostDirectives: [ShadowStylesDirective],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class ProfileAccountComponent {
  private readonly userService: UserService = inject(UserService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly colleaguesService: ColleaguesService = inject(ColleaguesService);

  protected readonly userInfo: WritableApiSignal<UserInfo> = toWritableApiSignal(this.userService.loadUser());

  protected readonly colleagues: WritableApiSignal<ColleagueInfo[]> = toWritableApiSignal(this.colleaguesService.loadColleagues());

  protected readonly inputConfig: UserInfoInputConfig = new UserInfoInputConfig(
    [
      {
        titleI18n: userInfoInputFields.commonI18n,
        inputs: userInfoInputFields.common,
      },
      {
        titleI18n: userInfoInputFields.personalI18n,
        inputs: userInfoInputFields.personal,
      },
      {
        titleI18n: userInfoInputFields.businessI18n,
        inputs: userInfoInputFields.business,
      },
      {
        titleI18n: userInfoInputFields.additionalI18n,
        inputs: userInfoInputFields.additional,
      },
    ],
    userInfoInputFields.personalCombinedRequiredInputs
  );

  protected readonly iconLibrary = iconLibrary;

  protected readonly loading: WritableSignal<boolean> = signal(false);

  constructor() {
    Events.colleaguesModified.observable().subscribe(() => refetchApiSignal(this.colleaguesService.loadColleagues(), this.colleagues));
    Events.userModified.observable().subscribe(() => refetchApiSignal(this.userService.loadUser(), this.userInfo));
  }

  protected edit(): void {
    this.dialogService.create(EditInfoDialogComponent, {
      inputConfig: this.inputConfig,
      userInfo: this.userInfo()!,
      saveAction: this.modifyUser
    });
  }

  private readonly modifyUser = async (userInfo: UserInfo): Promise<void> => {
    await firstValueFrom(this.userService.updateUser(userInfo));
    Events.userModified.next();
  }
}
